import { useState, useEffect, useCallback } from 'react';
import { Button, Input, Modal, Form, message, Pagination } from 'antd';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { apiFetch } from '../api';

const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalDocs, setTotalDocs] = useState(0);

  // Modal states for Add/Edit
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    try {
      const searchParam = searchTerm.trim();
      const query = `?page=${page}&limit=${limit}${searchParam ? `&search=${encodeURIComponent(searchParam)}` : ''}`;
      const response = await apiFetch(`/api/departments${query}`);
      const payload = await response.json();
      if (response.ok && payload.success) {
        setDepartments(payload.data || []);
        setTotalDocs(payload.totalDocs || 0);
      } else {
        message.error(payload.message || 'Failed to load departments.');
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      message.error('Failed to load departments.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const handleOpenAddModal = () => {
    setEditingId(null);
    form.resetFields();
    setShowModal(true);
  };

  const handleOpenEditModal = (record) => {
    setEditingId(record._id);
    form.setFieldsValue({ name: record.name });
    setShowModal(true);
  };

  const handleModalSubmit = async (values) => {
    setSubmitting(true);
    try {
      const url = editingId ? `/api/departments/${editingId}` : '/api/departments';
      const method = editingId ? 'PUT' : 'POST';
      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: values.name })
      });
      const payload = await response.json();
      if (response.ok && payload.success) {
        message.success(editingId ? 'Department updated successfully.' : 'Department added successfully.');
        setShowModal(false);
        form.resetFields();
        fetchDepartments();
      } else {
        message.error(payload.message || 'Failed to save department.');
      }
    } catch (error) {
      console.error('Error saving department:', error);
      message.error('Failed to save department.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id, name) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this department?',
      content: `This action cannot be undone and will delete the department "${name}" from the system.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const response = await apiFetch(`/api/departments/${id}`, {
            method: 'DELETE'
          });
          const payload = await response.json();
          if (response.ok && payload.success) {
            message.success('Department deleted successfully.');
            fetchDepartments();
          } else {
            message.error(payload.message || 'Failed to delete department.');
          }
        } catch (error) {
          console.error('Error deleting department:', error);
          message.error('Error occurred while deleting department.');
        }
      }
    });
  };

  return (
    <div className="wm-page">
      {/* Page Header */}
      <div className="wm-page-header">
        <div>
          <h1 className="wm-page-title dark:text-white">Department</h1>
        </div>
        <div className="flex flex-wrap gap-3 w-full lg:w-auto">
          <Button
            onClick={handleOpenAddModal}
            icon={<FiPlus />}
            size="large"
            type="primary"
            className="wm-btn-primary flex items-center h-10 px-6"
          >
            Add Department
          </Button>
        </div>
      </div>

      {/* Table Shell */}
      <div className="wm-table-shell dark:bg-slate-900">
        <div className="wm-card-header bg-[var(--wm-surface-muted)] dark:bg-slate-800/20">
          <div className="relative max-w-xs w-full flex gap-2">
            <Input
              size="large"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              className="w-full rounded-md bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
              suffix={<FiSearch className="text-slate-400" />}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-100 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700">
                <th className="px-4 py-3 text-[12px] font-medium text-slate-600 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap w-[75%]">
                  Department
                </th>
                <th className="px-4 py-3 text-[12px] font-medium text-slate-600 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {loading ? (
                [...Array(5)].map((_, idx) => (
                  <tr key={idx} className="border-b border-slate-200 dark:border-slate-800">
                    <td className="px-4 py-3">
                      <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-3/4"></div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center space-x-2">
                        <div className="h-8 w-8 bg-slate-200 dark:bg-slate-800 rounded-xl animate-pulse"></div>
                        <div className="h-8 w-8 bg-slate-200 dark:bg-slate-800 rounded-xl animate-pulse"></div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : departments.length === 0 ? (
                <tr>
                  <td colSpan="2" className="px-8 py-14 text-center text-slate-400 font-medium">
                    No departments found matching your search.
                  </td>
                </tr>
              ) : (
                departments.map((dept) => (
                  <tr key={dept._id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors group">
                    <td className="px-4 py-3 text-[13px] font-normal text-slate-800 dark:text-slate-100">
                      {dept.name}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center space-x-3">
                        <Button
                          onClick={() => handleOpenEditModal(dept)}
                          type="text"
                          icon={<FiEdit2 className="w-4 h-4" />}
                          className="text-slate-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-xl transition-all border-none"
                        />
                        <Button
                          onClick={() => handleDelete(dept._id, dept.name)}
                          type="text"
                          icon={<FiTrash2 className="w-4 h-4" />}
                          className="text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all border-none"
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Footer */}
      <div className="p-4 flex items-center justify-between">
        <div className="text-sm font-medium text-slate-500 dark:text-slate-400">
          Total Records Count is {totalDocs}
        </div>
        <Pagination
          current={page}
          pageSize={limit}
          total={totalDocs}
          showSizeChanger
          pageSizeOptions={[10, 25, 50, 100]}
          onChange={(p, ps) => { setPage(p); setLimit(ps); }}
          onShowSizeChange={(current, size) => { setPage(1); setLimit(size); }}
        />
      </div>

      {/* Add/Edit Modal */}
      <Modal
        title={<div className="text-[16px] font-medium text-slate-700">{editingId ? 'Edit Department' : 'Add Department'}</div>}
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        destroyOnHidden
        closeIcon={<FiX className="text-slate-500 w-5 h-5" />}
        width={500}
        styles={{
          header: { borderBottom: '1px solid #f0f0f0', padding: '16px 24px', margin: 0 },
          body: { padding: '24px' },
          content: { padding: 0, overflow: 'hidden', borderRadius: '8px' }
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleModalSubmit}
        >
          <Form.Item
            name="name"
            label={<span className="text-[14px] text-slate-700">Department</span>}
            rules={[{ required: true, message: 'Department name is required' }]}
            className="mb-8"
          >
            <Input
              size="large"
              className="rounded-md"
            />
          </Form.Item>

          <div className="pt-4 border-t border-slate-100 flex justify-end gap-3 -mx-6 px-6 -mb-6 pb-4 bg-white">
            <Button
              onClick={() => form.resetFields()}
              size="large"
              className="px-6 rounded-md bg-slate-50 border-slate-200 text-slate-400 hover:text-slate-500 hover:bg-slate-100"
            >
              Reset
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              size="large"
              className="px-6 rounded-md bg-[#005ea6] hover:bg-[#004d8a] text-white font-medium"
            >
              Save
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Departments;
